import { StyledText } from 'UI';
import { ConfirmModalProps } from 'UI/Modals/ConfirmModal/ConfirmModal';
import { StreakContent } from 'UI/Modals/PointsResultModal/components';
import { CouponDiscountType, SubscribeDtoTypeEnum } from 'api/generated';
import { AxiosError } from 'axios';
import { MAXIMUM_COURSE_POINTS } from 'constant/maximumPoints';
import { useAsyncAction, useGetProgressColor, useNotifications } from 'hooks';
import { Dispatch, ReactNode, SetStateAction, useDeferredValue, useEffect, useState } from 'react';
import { actions, selectors, useAppDispatch, useAppSelector } from 'store';
import { UnsubscribeSteps } from 'store/ducks/courses/types';
import styled, { css, useTheme } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';

import DiscountedPrice from '../components/DiscountedPrice';
import { ProgressColor } from './constants';

type StepContent = Pick<ConfirmModalProps, 'title' | 'text'> & {
  description?: ReactNode;
  leftButtonText: string;
  isButtonCSS?: boolean;
  rightButtonText: string;
  textColor?: string;
  personalTitleCSS?: ReturnType<typeof css>;
  component?: ReactNode;
  alsoJSX?: ReactNode;
  secondComponent?: ReactNode;
  onRightButtonClick: () => void;
  onLeftButtonClick: () => void;
  isLeftButtonLoading?: boolean;
};

export const useStepsContent = (
  onClose: () => void,
  onUnsubscribe: () => void,
  isLoading: boolean,
  setIsPause: Dispatch<SetStateAction<boolean>>,
  courseId: string | undefined,
  closeChooseProductModal: () => void,
): StepContent => {
  const dispatch = useAppDispatch();
  const { colors } = useTheme();
  const { errorToast, successToast } = useNotifications();
  const [promotion, setPromotion] = useState<number | undefined>();
  const [promotionType, setPromotionType] = useState<CouponDiscountType | undefined>();
  const [promotionFeedback, setPromotionFeedback] = useState<string>('');
  const [promotionFeedbackIsError, setPromotionFeedbackIsError] = useState<boolean>(false);
  const unsubscribeStep = useAppSelector(selectors.courses.selectUnsubscribeStep);
  const courseOptionsForPayment = useAppSelector(selectors.courses.selectCourseOptionsForPayment);
  const subscribeType = courseOptionsForPayment?.subscribeType;
  const [applyCancellationPromotion] = useAsyncAction(actions.payments.applyCancellationPromotion);
  const hasCancellationPromotion =
    typeof promotion === 'number' && promotion > 0 && typeof promotionType === 'string' && promotionType.length > 0;

  useEffect(() => {
    if (!courseOptionsForPayment?.courseId || subscribeType === undefined) {
      return;
    }

    setPromotion(courseOptionsForPayment.discount);
    setPromotionType(courseOptionsForPayment.discountType);
  }, [courseOptionsForPayment?.courseId]);

  const coursePoints = useAppSelector(selectors.performance.selectCourseStatistic);

  const points = coursePoints?.totalInPoints || 0;
  const deferredPoints = useDeferredValue(points);
  const value = (deferredPoints / MAXIMUM_COURSE_POINTS) * 100;

  const color = useGetProgressColor(value) as ProgressColor;

  useEffect(() => {
    if (
      color === '#55B47D' ||
      courseOptionsForPayment?.subscribeType === SubscribeDtoTypeEnum.Bronze ||
      courseOptionsForPayment?.subscribeType === SubscribeDtoTypeEnum.Silver
    ) {
      dispatch(actions.courses.setUnsubscribeStep(1));
    }
  }, [courseOptionsForPayment?.subscribeType]);

  const handleSubmit = () => {
    if (unsubscribeStep === UnsubscribeSteps.STEP_3 && !hasCancellationPromotion) {
      dispatch(actions.courses.setUnsubscribeStep(UnsubscribeSteps.STEP_5));
      return;
    }

    dispatch(actions.courses.setUnsubscribeStep(unsubscribeStep === 0 ? unsubscribeStep + 2 : unsubscribeStep + 1));
  };

  const handleCloseModal = () => {
    dispatch(actions.courses.setUnsubscribeStep(0));
    dispatch(actions.courses.selectCourseOptionsForPayment(null));
    onClose();
    if (unsubscribeStep === 2) {
      closeChooseProductModal();
    }
  };

  const handleUnsubscribe = async () => {
    try {
      await onUnsubscribe();
      dispatch(actions.courses.setUnsubscribeStep(0));
    } catch (error) {
      errorToast((error as AxiosError).message || 'Something went wrong');
    }
  };

  const handlePause = () => {
    setIsPause(true);
    closeChooseProductModal;
    dispatch(actions.courses.setUnsubscribeStep(0));
    onClose();
    if (unsubscribeStep === 2 || unsubscribeStep === 3) {
      closeChooseProductModal();
    }
  };

  const handlePromotion = async () => {
    if (!courseOptionsForPayment?.courseId) {
      return;
    }

    try {
      const result = await applyCancellationPromotion({ courseId: courseOptionsForPayment?.courseId });
      if (result?.alreadyApplied) {
        const message = result.message || 'Cancellation promotion is already applied to your subscription.';
        setPromotionFeedbackIsError(false);
        setPromotionFeedback(message);
        successToast(message);
        return;
      }

      const message = result?.message || 'Discount applied. It will be used on your next payment.';
      setPromotionFeedbackIsError(false);
      setPromotionFeedback(message);
      successToast(message);
      handleCloseModal();
    } catch (error) {
      const fallback = 'Unable to apply discount right now. You can still pause or cancel your subscription.';
      const fromError = error instanceof Error ? error.message : undefined;
      const fromAxios = (error as AxiosError<{ message?: string | string[] }>)?.response?.data?.message;
      const fromCause =
        error instanceof Error &&
        error.cause &&
        typeof error.cause === 'object' &&
        'message' in (error.cause as Record<string, unknown>)
          ? String((error.cause as Record<string, unknown>).message)
          : undefined;

      const message = Array.isArray(fromAxios)
        ? fromAxios[0] || fromCause || fromError || fallback
        : fromAxios || fromCause || fromError || fallback;
      setPromotionFeedbackIsError(true);
      setPromotionFeedback(message);
      errorToast(message);
    }
  };

  switch (unsubscribeStep) {
    default:
    case UnsubscribeSteps.STEP_1: {
      return {
        title: "Don't Lose Your Guarantees",
        personalTitleCSS: PersonalTitleCSS,
        textColor: colors.neutrals[1],
        isButtonCSS: true,
        description: (
          <Text>
            Please note that your progress is in {color === '#EB4040' ? 'red' : 'yellow'} zone. If you cancel, you will{' '}
            <BoldText>lose all progress</BoldText> and{' '}
            <BoldText>not qualify for the money back + retake fee pass guarantee.</BoldText>
          </Text>
        ),
        leftButtonText: 'I’ll stay',
        rightButtonText: 'Cancel anyway',
        onRightButtonClick: handleSubmit,
        onLeftButtonClick: handleCloseModal,
        component: <StreakContent isUnsubscribe={true} courseId={courseId} subscribeType={subscribeType} />,
      };
    }

    case UnsubscribeSteps.STEP_2: {
      return {
        title: 'Cancel subscription',
        textColor: colors.neutrals[1],
        personalTitleCSS: PersonalTitleCSS,
        isButtonCSS: true,
        description: (
          <Text>
            Please note that if you cancel your subscription,
            <br /> all your progress will be irretrievably lost.
          </Text>
        ),
        leftButtonText: 'I’ll stay',
        rightButtonText: 'Cancel anyway',
        onRightButtonClick: handleSubmit,
        onLeftButtonClick: handleCloseModal,
      };
    }

    case UnsubscribeSteps.STEP_3: {
      return {
        title: 'Pause Your Subscription',
        textColor: colors.neutrals[1],
        personalTitleCSS: PersonalTitleCSS,
        isButtonCSS: true,
        description: (
          <Text>
            Instead of cancelling and losing all your progress, we suggest<BoldText>pausing your subscription</BoldText>
            (1,2 or 3 months) until you are ready to study again.
          </Text>
        ),
        leftButtonText: 'Pause my subscription',
        rightButtonText: 'Cancel anyway',
        onRightButtonClick: handleSubmit,
        onLeftButtonClick: handlePause,
      };
    }

    case UnsubscribeSteps.STEP_4: {
      return {
        title: `Get ${promotionType === 'percentage' ? '%' : '$'}${promotion} off`,
        textColor: colors.neutrals[1],
        personalTitleCSS: PersonalTitleCSS,
        isButtonCSS: true,
        description: (
          <>
            <Text>
              We want to see you succeed!
              <BoldText>
                {promotionType === 'percentage' ? '%' : '$'}
                {promotion}
                off
              </BoldText>
              for the remainder of your subscription.
            </Text>
            {promotionFeedback ? (
              <PromotionFeedbackText $isError={promotionFeedbackIsError}>{promotionFeedback}</PromotionFeedbackText>
            ) : null}
          </>
        ),
        leftButtonText: `I’ll stay for ${promotionType === 'percentage' ? '%' : '$'}${promotion} off`,
        rightButtonText: 'Cancel anyway',
        onRightButtonClick: handleSubmit,
        onLeftButtonClick: handlePromotion,
        secondComponent: (
          <DiscountedPrice
            discount={promotion}
            discountType={promotionType}
            price={courseOptionsForPayment?.amount ? courseOptionsForPayment?.amount : 0}
          />
        ),
        alsoJSX: (
          <Buttons onClickCapture={handlePause}>
            <StatusContainer>
              <Status>{'Pause subscription instead'}</Status>
            </StatusContainer>
          </Buttons>
        ),
      };
    }

    case UnsubscribeSteps.STEP_5: {
      return {
        title: 'Are you sure you want to cancel your subscription?',
        text: 'You will not be able to undo this action',
        leftButtonText: 'Yes',
        rightButtonText: 'No',
        description: '',
        isLeftButtonLoading: isLoading,
        onRightButtonClick: handleCloseModal,
        onLeftButtonClick: handleUnsubscribe,
      };
    }
  }
};

const PersonalTitleCSS = css`
  margin-bottom: 0;
  ${({ theme: { typography, colors } }) => css`
    color: ${colors.primary[1]};
    ${typography.title_2_bold_32};
  `}

  ${respondToWidth.s`
    text-align: center;
  `}
`;

const Text = styled(StyledText)`
  text-align: center;
  margin-top: 24px;
  color: ${({ theme: { colors } }) => colors.neutrals[1]};
  ${({ theme: { typography } }) => typography.body_large_semibold_16};
  font-weight: 600 !important;

  ${({ theme: { typography } }) => respondToWidth.sm`
  ${typography.body_basic_semibold_14};
  margin-top: 30px;
  `};
`;

const PromotionFeedbackText = styled(StyledText)<{ $isError: boolean }>`
  margin-top: 12px;
  text-align: center;
  ${({ theme: { typography, colors }, $isError }) => css`
    ${typography.body_basic_semibold_14};
    color: ${$isError ? colors.system.red : colors.system.green};
  `}
`;

const BoldText = styled(Text)`
  display: inline;
  font-weight: bolder !important;
  margin-top: 0;
  margin-left: 4px;
  margin-right: 4px;
`;

const Buttons = styled.button`
  padding: 0 12px;
  margin-top: 20px;
  border: none;
  outline: none;
  background-color: inherit;
`;

const Status = styled(StyledText)`
  ${({ theme: { colors, typography } }) => css`
    white-space: nowrap;
    ${typography.body_large_semibold_16}
    color: ${colors.primary[1]};
  `}
`;

const StatusContainer = styled.div``;
